import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import {
  MessageSquare,
  FileText,
  Database,
  GitBranch,
  BookOpen,
  ListTodo,
  Plus,
  AtSign,
  Search,
  Upload,
  Download,
  RefreshCw,
  Gavel,
  Calendar,
  ChevronRight,
  CheckCircle2,
  Info,
  Coins,
  FileEdit
} from 'lucide-react';
import logoIcon from '@/assets/loop-icon.png';

interface UserGuideProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserGuide({ open, onOpenChange }: UserGuideProps) {
  const [activeSection, setActiveSection] = useState<string>('overview');

  const sections = [
    { id: 'overview', name: 'Overview', icon: Info },
    { id: 'chat', name: 'Chat', icon: MessageSquare },
    { id: 'documents', name: 'Documents', icon: FileText },
    { id: 'drafts', name: 'Drafts', icon: FileEdit },
    { id: 'uspto', name: 'USPTO Data', icon: Database },
    { id: 'examination', name: 'Examination', icon: GitBranch },
    { id: 'resources', name: 'Resources', icon: BookOpen },
    { id: 'tasks', name: 'Tasks', icon: ListTodo },
    { id: 'credits', name: 'Credits & Pricing', icon: Coins },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-[min(75vw,700px)] w-[min(75vw,700px)] h-[90vh] p-0 flex flex-col">
        <div className="flex flex-1 overflow-hidden w-full">
          {/* Sidebar Navigation */}
          <div className="w-56 border-r bg-gray-50 flex flex-col flex-shrink-0">
            <div className="p-4 border-b">
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <img src={logoIcon} alt="Patent Loop" className="h-8 w-auto" />
                  <DialogTitle className="text-lg">User Guide</DialogTitle>
                </div>
              </DialogHeader>
            </div>

            <ScrollArea className="flex-1 p-4">
              <div className="space-y-1">
                {sections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                        activeSection === section.id
                          ? 'bg-blue-100 text-blue-700 font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {section.name}
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </div>

          {/* Content Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <ScrollArea className="flex-1 h-full">
              <div className="p-8 min-h-full">
                {activeSection === 'overview' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">Patent Loop User Guide</h2>
                      <p className="text-gray-700 mb-4">
                        Patent Loop is a comprehensive patent analysis and prosecution management platform designed
                        to streamline your patent workflow with AI-powered analysis, document management, and
                        examination tools.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Platform Organization</h3>
                      <div className="space-y-3">
                        <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                          <MessageSquare className="w-5 h-5 text-blue-600 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-900">Chat</div>
                            <div className="text-sm text-gray-600">AI-powered patent analysis and Q&A</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                          <FileText className="w-5 h-5 text-purple-600 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-900">Documents</div>
                            <div className="text-sm text-gray-600">Manage patents, publications, and uploads</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                          <Database className="w-5 h-5 text-green-600 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-900">USPTO Data</div>
                            <div className="text-sm text-gray-600">View and manage USPTO applications</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg">
                          <GitBranch className="w-5 h-5 text-orange-600 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-900">Examination</div>
                            <div className="text-sm text-gray-600">Office action analysis and prosecution workflow</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg">
                          <BookOpen className="w-5 h-5 text-yellow-600 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-900">Resources</div>
                            <div className="text-sm text-gray-600">Access MPEP, CFR, and USC reference materials</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-pink-50 rounded-lg">
                          <ListTodo className="w-5 h-5 text-pink-600 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-900">Tasks</div>
                            <div className="text-sm text-gray-600">Run various tasks, such as for generating parts lists, Office Action reporting emails, finding support for claims, etc.</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Getting Started</h3>
                      <ol className="space-y-3 text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-blue-600 mt-0.5">1.</span>
                          <span>Add documents using the Chat or Documents page</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-blue-600 mt-0.5">2.</span>
                          <span>Use the Chat to ask questions about your documents</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-blue-600 mt-0.5">3.</span>
                          <span>Analyze office actions using the Examination page</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-blue-600 mt-0.5">4.</span>
                          <span>Reference legal materials using the Resources page</span>
                        </li>
                      </ol>
                    </div>
                  </div>
                )}

                {activeSection === 'chat' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        <MessageSquare className="inline w-6 h-6 mr-2 mb-1 text-blue-600" />
                        Chat Page
                      </h2>
                      <p className="text-gray-700">
                        The Chat page provides AI-powered analysis and question-answering capabilities for your patent documents.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Features</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Plus className="w-4 h-4 text-blue-600" />
                            Adding Documents
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Click the <strong>+ Add Documents</strong> button to add patents, publications, or applications to your session.
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Search by application number, patent number, or publication number</li>
                            <li>Upload PDF files directly</li>
                            <li>Documents are stored in your session for easy access</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <AtSign className="w-4 h-4 text-blue-600" />
                            Mentions
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Type <code className="bg-gray-100 px-1 rounded">@</code> in the chat to mention specific documents:
                          </p>
                          <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm">
                            <code>@US10123456 What is the main invention?</code>
                          </div>
                          <p className="text-xs text-gray-600 mt-2">
                            This ensures the AI focuses on the specific document you mention.
                          </p>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <MessageSquare className="w-4 h-4 text-blue-600" />
                            Asking Questions
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">Example questions you can ask:</p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>"What are the independent claims?"</li>
                            <li>"Summarize the invention in simple terms"</li>
                            <li>"What prior art is cited?"</li>
                            <li>"Compare claim 1 with claim 5"</li>
                            <li>"What is the filing date and status?"</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-blue-900 mb-1">Pro Tip</h4>
                          <p className="text-sm text-blue-800">
                            Be specific in your questions for better results. Instead of "Tell me about this patent,"
                            try "What problem does this invention solve?"
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'documents' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        <FileText className="inline w-6 h-6 mr-2 mb-1 text-purple-600" />
                        Documents Page
                      </h2>
                      <p className="text-gray-700">
                        The Documents page is your central hub for managing all patents, publications, applications, and uploaded files.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Document Types</h3>
                      <div className="space-y-3">
                        <div className="border-l-4 border-blue-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Patents</div>
                          <div className="text-sm text-gray-600">Granted US patents with full text and metadata</div>
                        </div>
                        <div className="border-l-4 border-green-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Publications</div>
                          <div className="text-sm text-gray-600">Published US patent applications</div>
                        </div>
                        <div className="border-l-4 border-orange-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Applications</div>
                          <div className="text-sm text-gray-600">USPTO applications with prosecution history</div>
                        </div>
                        <div className="border-l-4 border-cyan-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Foreign Patents</div>
                          <div className="text-sm text-gray-600">International patents from JP, EP, WO, and other jurisdictions</div>
                        </div>
                        <div className="border-l-4 border-purple-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Uploads</div>
                          <div className="text-sm text-gray-600">Your uploaded PDF documents (Pro/Premium only)</div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Features</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Search className="w-4 h-4 text-purple-600" />
                            Search and Filter
                          </h4>
                          <p className="text-sm text-gray-700">
                            Use the search bar to find documents by number, title, inventor, or assignee.
                            Filter by document type to narrow your results.
                          </p>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Upload className="w-4 h-4 text-purple-600" />
                            Upload Documents
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Click <strong>Add Documents</strong> to upload PDF files or fetch documents from USPTO:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Drag and drop PDF files or click to browse</li>
                            <li>Enter patent, publication, or application numbers</li>
                            <li>Documents are processed and added to your session</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <FileText className="w-4 h-4 text-purple-600" />
                            View Document Details
                          </h4>
                          <p className="text-sm text-gray-700">
                            Click on any document to view its full details, including claims, description,
                            drawings, and metadata.
                          </p>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Download className="w-4 h-4 text-purple-600" />
                            Download
                          </h4>
                          <p className="text-sm text-gray-700">
                            Use the three-dot menu on each document to download the PDF version.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        <Calendar className="inline w-5 h-5 mr-1 mb-1 text-blue-600" />
                        Daily Onboarding Limits
                      </h3>
                      <p className="text-sm text-gray-700 mb-4">
                        To ensure fair usage and system performance, Patent Loop implements daily limits for document onboarding.
                        These limits reset every day at midnight (UTC).
                      </p>

                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Patents, Publications & Foreign Patents</h4>
                          <p className="text-sm text-gray-700 mb-2">
                            US Patents, US Publications, and Foreign Patents (JP, EP, WO, etc.) all count toward the same daily limit:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li><strong>Free Tier:</strong> 10 documents per day</li>
                            <li><strong>Pro Tier ($19/mo):</strong> 25 documents per day</li>
                            <li><strong>Premium Tier ($47/mo):</strong> 100 documents per day</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Document Uploads</h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Uploaded documents have a separate daily limit and use credits based on file size:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li><strong>Free Tier:</strong> Not available</li>
                            <li><strong>Pro Tier ($19/mo):</strong> 10 uploads per day</li>
                            <li><strong>Premium Tier ($47/mo):</strong> 25 uploads per day + OCR capability</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Batch Onboarding</h4>
                          <p className="text-sm text-gray-700">
                            When using the <strong>Batch Mode</strong> (accessible from the Add Documents button),
                            you can onboard multiple documents at once. The system processes 3 documents simultaneously
                            until all are complete or your daily limit is reached. If you hit your limit during batch
                            processing, you'll see a notification and remaining documents won't be processed.
                          </p>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Viewing Your Usage</h4>
                          <p className="text-sm text-gray-700">
                            You can track your daily onboarding usage in the <strong>Credits & Usage</strong> modal
                            (accessible from the user menu in the header). This shows:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Current usage vs. daily limit for each category</li>
                            <li>Remaining quota for today</li>
                            <li>Percentage of daily limit used</li>
                            <li>Comparison of limits across subscription tiers</li>
                          </ul>
                        </div>

                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                          <div className="flex items-start gap-2">
                            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-900">
                              <strong>Note:</strong> Daily limits only apply to onboarding new documents.
                              Once a document is onboarded, you can access, analyze, and use it unlimited times
                              without consuming additional quota.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'drafts' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        <FileEdit className="inline w-6 h-6 mr-2 mb-1 text-indigo-600" />
                        Drafts
                      </h2>
                      <div className="mb-3 p-3 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg">
                        <div className="flex items-center gap-2 text-purple-700 font-semibold mb-1">
                          <span className="text-lg">⭐</span>
                          <span>Premium Feature</span>
                        </div>
                        <p className="text-sm text-purple-600">
                          Drafts is available exclusively for Premium account holders. Upgrade your account to access draft creation, editing, and export features.
                        </p>
                      </div>
                      <p className="text-gray-700">
                        Create, edit, and manage patent draft documents with line-numbered editing and professional DOCX export capabilities.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Accessing Drafts</h3>
                      <p className="text-sm text-gray-700 mb-3">
                        You can access and work with drafts in three different ways:
                      </p>
                      <div className="space-y-3">
                        <div className="border-l-4 border-indigo-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Drafts Page</div>
                          <div className="text-sm text-gray-600">
                            Click the Drafts icon (📝) in the top navigation bar (next to notifications) to open the dedicated drafts workspace
                          </div>
                        </div>
                        <div className="border-l-4 border-blue-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Chat Interface</div>
                          <div className="text-sm text-gray-600">
                            Generate and edit draft content within the chat area
                          </div>
                        </div>
                        <div className="border-l-4 border-orange-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Examination Page</div>
                          <div className="text-sm text-gray-600">
                            Open drafts alongside office actions and other documents in the examination workspace
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Creating Drafts</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Plus className="w-4 h-4 text-indigo-600" />
                            Create from Drafts Page
                          </h4>
                          <ol className="text-sm text-gray-700 list-decimal list-inside ml-4 space-y-1">
                            <li>Click the <strong>Drafts icon</strong> in the top navigation</li>
                            <li>Click <strong>+ New Draft</strong> button</li>
                            <li>Enter a title for your draft</li>
                            <li>Select a draft type (General, Specification, Claims, Background, Summary, Description)</li>
                            <li>Click <strong>Create Draft</strong></li>
                          </ol>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <MessageSquare className="w-4 h-4 text-indigo-600" />
                            Generate from Chat
                          </h4>
                          <p className="text-sm text-gray-700">
                            Ask the AI to generate draft content (e.g., "Draft claims for..."), then save the generated content as a draft.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Draft Features</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <FileEdit className="w-4 h-4 text-indigo-600" />
                            Line-Numbered Editor
                          </h4>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Every line displays a line number on the left for easy reference</li>
                            <li>Click line numbers to jump to specific lines</li>
                            <li>Professional layout similar to legal/patent document editors</li>
                            <li>Real-time character and word count</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Download className="w-4 h-4 text-indigo-600" />
                            Export to DOCX
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Export drafts to Microsoft Word format:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Click <strong>Export DOCX</strong> button in Drafts Page</li>
                            <li>Or click <strong>Export</strong> icon when viewing in Examination Page</li>
                            <li>File downloads automatically with proper formatting</li>
                            <li>Includes title, draft type, metadata, and full content</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <GitBranch className="w-4 h-4 text-indigo-600" />
                            Multi-Document Integration
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            In the Examination Page, open drafts alongside other documents:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li><strong>Side-by-side:</strong> Draft + Office Action for claim amendments</li>
                            <li><strong>Three-way:</strong> Draft + Prior Art + Office Action for comparison</li>
                            <li><strong>Multi-draft:</strong> Work on Spec + Claims simultaneously</li>
                            <li>Arrange in horizontal, vertical, or grid layouts</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Draft Types</h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                          <div className="font-medium text-sm text-gray-900">General</div>
                          <div className="text-xs text-gray-600">Freeform drafting for any purpose</div>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                          <div className="font-medium text-sm text-gray-900">Specification</div>
                          <div className="text-xs text-gray-600">Patent specification sections</div>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                          <div className="font-medium text-sm text-gray-900">Claims</div>
                          <div className="text-xs text-gray-600">Independent and dependent claims</div>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                          <div className="font-medium text-sm text-gray-900">Background</div>
                          <div className="text-xs text-gray-600">Background of the invention</div>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                          <div className="font-medium text-sm text-gray-900">Summary</div>
                          <div className="text-xs text-gray-600">Summary of the invention</div>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                          <div className="font-medium text-sm text-gray-900">Description</div>
                          <div className="text-xs text-gray-600">Detailed description sections</div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Managing Drafts</h3>
                      <div className="space-y-3">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-1 text-sm">Viewing All Drafts</h4>
                          <p className="text-sm text-gray-600">
                            The Drafts Page shows all your drafts in a list with title, content preview, type badge, and last updated date.
                          </p>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 mb-1 text-sm">Editing</h4>
                          <p className="text-sm text-gray-600">
                            Click any draft to open it for editing. Changes must be saved manually using the <strong>Save</strong> button.
                          </p>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 mb-1 text-sm">Deleting</h4>
                          <p className="text-sm text-gray-600">
                            Hover over a draft in the list and click the trash icon (🗑️) to delete it permanently.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Best Practices</h3>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-gray-700">
                            <strong>Use descriptive titles</strong> - Include application number or invention name
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-gray-700">
                            <strong>Save frequently</strong> - Click Save after significant changes (no auto-save)
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-gray-700">
                            <strong>Export regularly</strong> - Keep DOCX backups for external review
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-gray-700">
                            <strong>Use multi-document view</strong> - Open drafts with related documents in Examination Page
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-gray-700">
                            <strong>Leverage line numbers</strong> - Use for navigation and referencing specific sections
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-indigo-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-indigo-900 mb-1">Workflow Tip</h4>
                          <p className="text-sm text-indigo-800">
                            When responding to office actions, open your draft alongside the office action in the Examination Page.
                            This allows you to make amendments while viewing rejections, creating a more efficient workflow than
                            switching between separate pages.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'uspto' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        <Database className="inline w-6 h-6 mr-2 mb-1 text-green-600" />
                        USPTO Data Page
                      </h2>
                      <p className="text-gray-700">
                        View and manage USPTO patent applications with detailed information and prosecution documents.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Features</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Database className="w-4 h-4 text-green-600" />
                            Application List
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            The left panel shows all applications in your session with:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Status badges (Granted, Pending, Abandoned)</li>
                            <li>Application number and title</li>
                            <li>Search functionality to find specific applications</li>
                            <li>Filter by status</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <RefreshCw className="w-4 h-4 text-green-600" />
                            Refresh Application Data
                          </h4>
                          <p className="text-sm text-gray-700">
                            Click the <strong>Refresh</strong> button to fetch the latest data from USPTO,
                            including new prosecution documents and status updates.
                          </p>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <FileText className="w-4 h-4 text-green-600" />
                            Application Details
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            The main panel displays comprehensive information:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Filing date, inventors, assignee, and examiner</li>
                            <li>Current status and patent/publication numbers</li>
                            <li>Bibliographic data</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Download className="w-4 h-4 text-green-600" />
                            Prosecution Documents
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            View and download all prosecution documents:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Office actions and responses</li>
                            <li>Claims, specifications, and drawings</li>
                            <li>Notices and correspondence</li>
                            <li>Color-coded by document type</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Download className="w-4 h-4 text-green-600" />
                            Export to Excel
                          </h4>
                          <p className="text-sm text-gray-700">
                            Export application data to Excel for reporting and analysis.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'examination' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        <GitBranch className="inline w-6 h-6 mr-2 mb-1 text-orange-600" />
                        Examination Page
                      </h2>
                      <p className="text-gray-700">
                        The Examination page is your workspace for analyzing office actions and managing prosecution workflows.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Layout Overview</h3>
                      <div className="space-y-3">
                        <div className="border border-gray-200 rounded-lg p-3">
                          <div className="font-medium text-gray-900 mb-1">Left Panel: Application Details</div>
                          <div className="text-sm text-gray-600">
                            Select an application and view its metadata, filing information, and status
                          </div>
                        </div>
                        <div className="border border-gray-200 rounded-lg p-3">
                          <div className="font-medium text-gray-900 mb-1">Center Panel: Document Viewers</div>
                          <div className="text-sm text-gray-600">
                            View up to 4 documents simultaneously (office actions, responses, specs, references)
                          </div>
                        </div>
                        <div className="border border-gray-200 rounded-lg p-3">
                          <div className="font-medium text-gray-900 mb-1">Right Panel: Analysis Results</div>
                          <div className="text-sm text-gray-600">
                            Display AI-powered analysis results and task outputs
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Features</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <RefreshCw className="w-4 h-4 text-orange-600" />
                            Refresh Application
                          </h4>
                          <p className="text-sm text-gray-700">
                            Click <strong>Refresh</strong> in the application details header to fetch the latest
                            data from USPTO, including new office actions and prosecution documents.
                          </p>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <FileText className="w-4 h-4 text-orange-600" />
                            Document Selection
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Select documents from the dropdown menus:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li><strong>Office Action:</strong> Select examiner actions and rejections</li>
                            <li><strong>Pros Doc:</strong> Select specifications, claims, drawings, and responses</li>
                            <li><strong>Reference:</strong> Add patents, publications, or legal references</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Gavel className="w-4 h-4 text-orange-600" />
                            Analysis Tools
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Click <strong>Analysis</strong> to run AI-powered tasks:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li><strong>Extract Office Action Info:</strong> Extract key dates, rejections, and requirements</li>
                            <li><strong>Analyze Issues:</strong> Detailed analysis of each rejection and claim</li>
                            <li><strong>Summarize Prior Art:</strong> Summarize cited references</li>
                            <li><strong>Generate OA Reporting Email:</strong> Create client reporting emails</li>
                            <li><strong>Build Timeline:</strong> Create prosecution history timeline</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-orange-600" />
                            Task History
                          </h4>
                          <p className="text-sm text-gray-700">
                            Click <strong>View History</strong> in the analysis panel to see all previous
                            analysis results for the current document or application.
                          </p>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <BookOpen className="w-4 h-4 text-orange-600" />
                            Reference Documents
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Add reference documents for comparison:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Search for patents or publications</li>
                            <li>Browse MPEP sections</li>
                            <li>Browse CFR and USC sections</li>
                            <li>View your uploaded documents</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-orange-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-orange-900 mb-1">Workflow Tip</h4>
                          <p className="text-sm text-orange-800">
                            Start with "Extract Office Action Info" to get an overview, then use "Analyze Issues"
                            for detailed claim-by-claim analysis. Use "Generate OA Reporting Email" to create
                            client communications.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'resources' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        <BookOpen className="inline w-6 h-6 mr-2 mb-1 text-yellow-600" />
                        Resources Page
                      </h2>
                      <p className="text-gray-700">
                        Access legal reference materials including MPEP, CFR, and USC for patent prosecution research.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Available Resources</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">MPEP (Manual of Patent Examining Procedure)</h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Browse or search the complete MPEP:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Navigate by chapter and section</li>
                            <li>Search for specific section numbers (e.g., "2141")</li>
                            <li>Full text with formatting preserved</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">CFR (Code of Federal Regulations)</h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Access Title 37 - Patents, Trademarks, and Copyrights:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Browse by part and subpart</li>
                            <li>Search for specific sections (e.g., "1.111")</li>
                            <li>View regulation text and requirements</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">USC (United States Code)</h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Reference Title 35 - Patents:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Browse patent statutes by section</li>
                            <li>Search for specific sections (e.g., "103")</li>
                            <li>View statutory language</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">How to Use</h3>
                      <ol className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-yellow-600 mt-0.5">1.</span>
                          <span>Select a resource type from the tabs (MPEP, CFR, or USC)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-yellow-600 mt-0.5">2.</span>
                          <span>Enter a section number in the search box or browse the table of contents</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-yellow-600 mt-0.5">3.</span>
                          <span>Click on a section to view its full text</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-yellow-600 mt-0.5">4.</span>
                          <span>Use the reference in the Examination page for side-by-side comparison</span>
                        </li>
                      </ol>
                    </div>
                  </div>
                )}

                {activeSection === 'tasks' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        <ListTodo className="inline w-6 h-6 mr-2 mb-1 text-pink-600" />
                        Tasks Page
                      </h2>
                      <p className="text-gray-700">
                        Tasks are guided workflows that package proven prompts and output layouts to generate consistent patent deliverables with a few clicks.
                        Each task is optimized for specific patent prosecution needs.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Document Analysis Tasks</h3>
                      <div className="space-y-3">
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Parts List</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Automatically generates a comprehensive list of parts or components identified in a patent document.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Quickly identify all reference numerals and their corresponding components for specification writing or claim drafting.
                          </p>
                        </div>

                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Specification Map</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Maps specification details to relevant parts and features, creating connections between described elements and drawing references.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Ensure complete specification support for all claim elements and identify gaps in description.
                          </p>
                        </div>

                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Claims Support</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Analyzes claims for specification support, verifying that all claim elements have adequate written description.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Verify 35 U.S.C. § 112(a) written description compliance before filing or during prosecution.
                          </p>
                        </div>

                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Add Numerals to Claims</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Automatically adds reference numerals to claims that correspond to parts or components shown in the drawings.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Prepare claims with reference numerals for patent applications per 37 CFR 1.75(d)(2).
                          </p>
                        </div>

                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Custom Analysis</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Run custom analysis on patent documents with user-defined prompts for specialized research needs.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Perform specialized analysis not covered by standard tasks, such as freedom-to-operate analysis or technical feature extraction.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Invention Record Tasks</h3>
                      <div className="space-y-3">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">IR Metadata Extraction</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Extracts metadata from invention records including inventors, assignees, filing dates, and technical classifications.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Quickly populate application data sheets (ADS) and organize invention disclosure information.
                          </p>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">IR Innovation Analysis</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Analyzes innovation aspects and technological advancements described in invention records.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Identify patentable subject matter and distinguish novel features from conventional elements.
                          </p>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">IR Technology Classification</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Classifies invention records into appropriate technology categories and suggests relevant CPC/USPC classifications.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Determine proper patent classifications for prior art searching and application routing.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Patent Drafting Tasks</h3>
                      <div className="space-y-3">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Draft Detailed Description</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Generates comprehensive detailed description sections from invention records, user input, and context documents.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Create well-structured detailed descriptions that satisfy enablement and written description requirements.
                          </p>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Draft Claims</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Generates patent claims from disclosure text with proper claim formatting, dependencies, and structure.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Draft independent and dependent claims with proper antecedent basis and claim differentiation.
                          </p>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Draft Summary</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Generates summary of the invention section from disclosure text, highlighting key features and advantages.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Create concise summaries that introduce the invention and its benefits.
                          </p>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Draft Background</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Generates background section describing the technical field and problems addressed by the invention.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Draft background sections that establish context without admitting prior art.
                          </p>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Draft Other Embodiments</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Generates alternative embodiments and variations based on the primary invention description.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Expand claim scope by describing variations, modifications, and alternative implementations.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Office Action Tasks</h3>
                      <div className="space-y-3">
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Extract Office Action Info</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Extracts key information from office actions including deadlines, rejections, objections, and requirements.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Quickly identify critical deadlines and action items from lengthy office actions.
                          </p>
                        </div>

                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Analyze Office Action Issues</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Provides detailed claim-by-claim analysis of all rejections, objections, and examiner positions.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Understand examiner's reasoning and identify response strategies for each rejection.
                          </p>
                        </div>

                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Summarize Office Action Prior Art</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Summarizes all prior art references cited in the office action with relevant teachings and figures.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Quickly understand cited references and identify distinguishing features.
                          </p>
                        </div>

                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Build Office Action Timeline</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Creates a chronological timeline of the prosecution history from office action documents.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Track prosecution progress and understand the examination history at a glance.
                          </p>
                        </div>

                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Generate OA Reporting Email</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Generates professional email reports summarizing office action information for client communication.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Create clear, client-friendly summaries of office actions with next steps and deadlines.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Document Processing Tasks</h3>
                      <div className="space-y-3">
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Translate Document</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Translates entire patent documents or technical documents between languages while preserving technical accuracy and legal terminology.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Translate foreign patents for prior art analysis or prepare translations for PCT filings.
                          </p>
                        </div>

                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">LaTeX to Word Converter</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Converts LaTeX documents (ZIP archives) to Microsoft Word format while preserving mathematical equations and formatting.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Convert technical documents with complex equations to Word for USPTO filing requirements.
                          </p>
                        </div>

                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">PDF OCR</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Extracts text from scanned PDF documents using optical character recognition. Output as text, Word (DOCX), or JSON with positional data.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Convert scanned documents to searchable, editable text for analysis and processing.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">How to Use Tasks</h3>
                      <ol className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-pink-600 mt-0.5">1.</span>
                          <span>Select a task from the left panel or use the search and filter to find specific tasks</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-pink-600 mt-0.5">2.</span>
                          <span>Fill in required inputs such as document selections, text input, or configuration options</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-pink-600 mt-0.5">3.</span>
                          <span>Click Submit to run the task - credit cost will be shown if significant</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-pink-600 mt-0.5">4.</span>
                          <span>View results in the task panel or access them later from task history</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-pink-600 mt-0.5">5.</span>
                          <span>Add task results to Chat for further analysis or export to Word/PDF</span>
                        </li>
                      </ol>
                    </div>

                    <Separator />

                    <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-pink-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-pink-900 mb-1">Task History & Reusability</h4>
                          <p className="text-sm text-pink-800 mb-2">
                            All task results are saved in task history and can be reviewed at any time without additional credits.
                            You can also add previous task results to your chat for follow-up questions or refinements.
                          </p>
                          <p className="text-sm text-pink-800">
                            <strong>Tip:</strong> Review task history before re-running the same task to save credits.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'credits' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        <Coins className="inline w-6 h-6 mr-2 mb-1 text-blue-600" />
                        Credits & Pricing
                      </h2>
                      <p className="text-gray-700">
                        Patent Loop uses a credit-based system to provide fair and transparent pricing for AI-powered features.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">What Are Credits?</h3>
                      <p className="text-sm text-gray-700 mb-3">
                        Credits are the currency used within Patent Loop to access AI-powered analysis, document processing,
                        and other premium features. Each action that uses AI or computational resources consumes a certain
                        number of credits based on the complexity and resources required.
                      </p>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                          <div>
                            <h4 className="font-medium text-blue-900 mb-1">Check Your Balance</h4>
                            <p className="text-sm text-blue-800">
                              Your current credit balance is always displayed in the header at the top right of the screen.
                              Click on it to view detailed usage history and purchase more credits.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">What Credits Are Used For</h3>
                      <div className="space-y-3">
                        <div className="border-l-4 border-purple-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">AI Chat Queries</div>
                          <div className="text-sm text-gray-600">
                            Each question you ask in the Chat page consumes credits based on the length and complexity
                            of the query and response
                          </div>
                        </div>
                        <div className="border-l-4 border-green-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Document Analysis</div>
                          <div className="text-sm text-gray-600">
                            Processing and analyzing patents, publications, and uploaded documents
                          </div>
                        </div>
                        <div className="border-l-4 border-orange-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Office Action Analysis</div>
                          <div className="text-sm text-gray-600">
                            Running tasks like Extract OA Info, Analyze Issues, Summarize Prior Art, and Generate Reporting Emails
                          </div>
                        </div>
                        <div className="border-l-4 border-blue-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Drafting Tasks</div>
                          <div className="text-sm text-gray-600">
                            Generating claims, specifications, parts lists, and other patent drafting content
                          </div>
                        </div>
                        <div className="border-l-4 border-pink-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Document Processing</div>
                          <div className="text-sm text-gray-600">
                            Loading and processing documents from USPTO, including fetching prosecution history
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Credit Costs</h3>
                      <p className="text-sm text-gray-700 mb-3">
                        Credit costs vary based on the complexity and computational resources required:
                      </p>
                      <ul className="text-sm text-gray-700 list-disc list-inside ml-4 space-y-2">
                        <li><strong>Simple queries:</strong> Fewer credits for basic questions or short responses</li>
                        <li><strong>Complex analysis:</strong> More credits for detailed analysis of office actions or multi-document comparisons</li>
                        <li><strong>Large documents:</strong> Processing longer documents consumes more credits</li>
                        <li><strong>Batch operations:</strong> Running multiple tasks or analyzing entire prosecution histories</li>
                      </ul>
                      <p className="text-sm text-gray-600 mt-3">
                        The exact credit cost is shown before running tasks that consume significant credits,
                        allowing you to make informed decisions.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Purchasing Credits</h3>
                      <div className="space-y-3">
                        <p className="text-sm text-gray-700">
                          To purchase more credits:
                        </p>
                        <ol className="text-sm text-gray-700 list-decimal list-inside ml-4 space-y-2">
                          <li>Click on your credit balance in the top right corner of the header</li>
                          <li>Click the "Purchase Credits" button in the credits modal</li>
                          <li>Select a credit package that fits your needs</li>
                          <li>Complete the secure checkout process</li>
                          <li>Credits are added to your account immediately upon successful payment</li>
                        </ol>
                      </div>
                    </div>

                    <Separator />

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-green-900 mb-1">Credit Usage Tips</h4>
                          <ul className="text-sm text-green-800 space-y-1 mt-2">
                            <li>• Be specific in your queries to get better results with fewer follow-up questions</li>
                            <li>• Use @mentions to focus AI responses on specific documents</li>
                            <li>• Review task results history to avoid re-running the same analysis</li>
                            <li>• Monitor your credit balance regularly to avoid interruptions</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            <div className="border-t p-4 bg-gray-50 flex-shrink-0">
              <div className="flex justify-end">
                <Button onClick={() => onOpenChange(false)}>
                  Close Guide
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
